import { useContext, useMemo } from 'react';
import {
  Outlet,
  Route,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import { RouteErrorBoundary } from '@/app/ui/ErrorBoundary';
import {
  PageRoute,
  advertiserRoutes,
  backofficeRoutes,
  commonRoutes,
  locationRoutes,
  servicesRoutes,
} from '@/config/routes';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import NotFound from '@/shared/ui/NotFound';
import { NotFoundProvider, useNotFound } from '@/shared/ui/NotFound/NotFoundContext';

import AuthorizedRoutes from './AuthorizedRoutes';
import AuthorizedDeniedRoutes from './AuthorizedRoutes/AuthorizedDeniedRoutes';
import AuthorizedFullScreenRouters from './AuthorizedRoutes/AuthorizedFullScreenRoutes';

const USER_TYPE_ROUTES_MAP: Record<string, PageRoute[]> = {
  location: locationRoutes,
  service: servicesRoutes,
  advertiser: advertiserRoutes,
  backoffice: backofficeRoutes,
};

const ALL_USER_TYPE_ROUTES = [
  ...locationRoutes,
  ...servicesRoutes,
  ...advertiserRoutes,
  ...backofficeRoutes,
];

function getRoutesByUserType(userType: string | undefined, fullScreen: boolean): PageRoute[] {
  const userTypeRoutes =
    (userType ? USER_TYPE_ROUTES_MAP[userType] : undefined) ?? backofficeRoutes;
  const filterFn = (route: PageRoute) => Boolean(route.fullScreen) === fullScreen;

  return [...commonRoutes.filter(filterFn), ...userTypeRoutes.filter(filterFn)];
}

function getDeniedPaths(userType: string | undefined): string[] {
  const allowedRoutes = [
    ...getRoutesByUserType(userType, false),
    ...getRoutesByUserType(userType, true),
  ];
  const allowedPaths = new Set(allowedRoutes.map((r) => r.path));

  return ALL_USER_TYPE_ROUTES.map((r) => r.path).filter((path) => !allowedPaths.has(path));
}

function renderRoutes(routes: PageRoute[]) {
  return routes.map(({ path, component: Component }) => (
    <Route key={path} path={path} element={<Component />} />
  ));
}

function renderDeniedRoutes(paths: string[]) {
  return paths.map((path) => <Route key={path} path={path} element={null} />);
}

const ALL_KNOWN_PATHS = [
  ...commonRoutes.map((r) => r.path),
  ...ALL_USER_TYPE_ROUTES.map((r) => r.path),
];

const NotFoundGate = () => {
  const { notFound } = useNotFound();
  return notFound ? <NotFound /> : <Outlet />;
};

const NotFoundLayout = () => (
  <NotFoundProvider routes={ALL_KNOWN_PATHS}>
    <NotFoundGate />
  </NotFoundProvider>
);

const Routes = () => {
  const userType = useContext(UserTypeContext);

  const router = useMemo(() => createBrowserRouter(buildAppRoutes(userType)), [userType]);

  return <RouterProvider router={router} />;
};

export default Routes;

export function buildAppRoutes(userType: string | undefined): RouteObject[] {
  const routes = getRoutesByUserType(userType, false);
  const fullScreenRoutes = getRoutesByUserType(userType, true);
  const deniedPaths = getDeniedPaths(userType);

  // The pathless errorElement routes sit inside each layout, so a page that
  // throws shows the error page inside the shell (sidebar kept) and the next
  // navigation clears it. The root errorElement only catches the shell itself.
  return createRoutesFromElements(
    <Route element={<NotFoundLayout />} errorElement={<RouteErrorBoundary fullScreen />}>
      <Route element={<AuthorizedRoutes />}>
        <Route errorElement={<RouteErrorBoundary />}>{renderRoutes(routes)}</Route>
      </Route>
      <Route element={<AuthorizedFullScreenRouters />}>
        <Route errorElement={<RouteErrorBoundary fullScreen />}>
          {renderRoutes(fullScreenRoutes)}
        </Route>
      </Route>
      <Route element={<AuthorizedDeniedRoutes />}>{renderDeniedRoutes(deniedPaths)}</Route>
      <Route element={<AuthorizedRoutes />}>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  );
}
